import { useLazyQuery, useMutation, useQuery } from "@apollo/client";

import {
  CHECK_IF_PACKAGES_EXIST_FOR_PACKAGE_TYPE_QUERY,
  CREATE_FINANCE_PACKAGE_MUTATION,
  CREATE_FINANCE_PACKAGE_TYPE_MUTATION,
  DELETE_FINANCE_PACKAGE_MUTATION,
  DELETE_FINANCE_PACKAGE_TYPE_MUTATION,
  GET_FINANCE_PACKAGE_QUERY,
  GET_FINANCE_SETTINGS_QUERY,
  LIST_FINANCE_PACKAGES_QUERY,
  LIST_FINANCE_PACKAGE_TYPES_QUERY,
  SAVE_FINANCE_SETTINGS_MUTATION,
  UPDATE_FINANCE_PACKAGE_MUTATION,
  UPDATE_FINANCE_PACKAGE_TYPE_MUTATION,
} from "~/api";
import type {
  FinancePackage,
  FinancePackageType,
  FinanceQuoteLineItem,
  FinanceSettings,
} from "~/types/finance";
import { packageToLineItems } from "~/features/Settings/company/finance/utils/packageLineItems";
import { errorsHandler } from "~/transformers";

import {
  removeFinancePackageFromListCache,
  upsertFinancePackageInListCache,
} from "./packageCache";

export const useGetFinanceSettings = (skip = false) => {
  const { data, loading, error, refetch } = useQuery<{
    getFinanceSettings: FinanceSettings | null;
  }>(GET_FINANCE_SETTINGS_QUERY, { skip });

  return {
    financeSettings: data?.getFinanceSettings ?? null,
    loading,
    error,
    refetch,
  };
};

export const useSaveFinanceSettings = () => {
  const [saveSettings, { loading, error }] = useMutation(
    SAVE_FINANCE_SETTINGS_MUTATION,
  );

  return {
    saveFinanceSettings: (input: Record<string, unknown>) =>
      new Promise<FinanceSettings | null>(async (resolve, reject) => {
        try {
          const { data, errors } = await saveSettings({
            variables: { input },
          });
          if (errors?.length || !data?.saveFinanceSettings) {
            reject(
              errorsHandler({
                graphQLErrors: errors ?? [
                  { message: "Failed to save finance settings" },
                ],
              }),
            );
            return;
          }
          resolve(data.saveFinanceSettings);
        } catch (err) {
          reject(errorsHandler(err));
        }
      }),
    loading,
    error,
  };
};

export const useListFinancePackageTypes = (skip = false) => {
  const { data, loading, error, refetch } = useQuery<{
    listFinancePackageTypes: FinancePackageType[];
  }>(LIST_FINANCE_PACKAGE_TYPES_QUERY, { skip });

  return {
    packageTypes: data?.listFinancePackageTypes ?? [],
    loading,
    error,
    refetch,
  };
};

export const useListFinancePackages = (skip = false) => {
  const { data, loading, error, refetch } = useQuery<{
    listFinancePackages: FinancePackage[];
  }>(LIST_FINANCE_PACKAGES_QUERY, { skip });

  return {
    packages: data?.listFinancePackages ?? [],
    loading,
    error,
    refetch,
  };
};

export const useGetFinancePackage = (id?: string) => {
  const { data, loading, error, refetch } = useQuery<{
    getFinancePackage: FinancePackage | null;
  }>(GET_FINANCE_PACKAGE_QUERY, {
    skip: !id,
    variables: { id },
  });

  return {
    financePackage: data?.getFinancePackage ?? null,
    loading,
    error,
    refetch,
  };
};

export const useCreateFinancePackageType = () => {
  const [mutate, { loading }] = useMutation(
    CREATE_FINANCE_PACKAGE_TYPE_MUTATION,
  );

  return {
    createPackageType: (name: string) =>
      new Promise(async (resolve, reject) => {
        try {
          const result = await mutate({
            variables: { input: { name } },
            refetchQueries: [{ query: LIST_FINANCE_PACKAGE_TYPES_QUERY }],
          });
          resolve(result);
        } catch (err) {
          reject(errorsHandler(err));
        }
      }),
    loading,
  };
};

export const useUpdateFinancePackageType = () => {
  const [mutate, { loading }] = useMutation(
    UPDATE_FINANCE_PACKAGE_TYPE_MUTATION,
  );

  return {
    updatePackageType: (input: { _id: string; name: string }) =>
      new Promise(async (resolve, reject) => {
        try {
          const result = await mutate({
            variables: { input },
            refetchQueries: [{ query: LIST_FINANCE_PACKAGE_TYPES_QUERY }],
          });
          resolve(result);
        } catch (err) {
          reject(errorsHandler(err));
        }
      }),
    loading,
  };
};

export const useCheckPackagesExistForPackageType = () => {
  const [checkPackages] = useLazyQuery(
    CHECK_IF_PACKAGES_EXIST_FOR_PACKAGE_TYPE_QUERY,
    { fetchPolicy: "network-only" },
  );

  return {
    checkIfPackageTypeUsed: async (packageTypeId: string) => {
      const { data } = await checkPackages({
        variables: { packageTypeId },
      });
      return !!data?.checkIfPackagesExistForPackageType;
    },
  };
};

export const useDeleteFinancePackageType = () => {
  const [mutate, { loading }] = useMutation(
    DELETE_FINANCE_PACKAGE_TYPE_MUTATION,
  );

  return {
    deletePackageType: (id: string, transferToPackageTypeId?: string | null) =>
      new Promise(async (resolve, reject) => {
        try {
          const result = await mutate({
            variables: {
              id,
              input: transferToPackageTypeId
                ? { transferToPackageTypeId }
                : undefined,
            },
            refetchQueries: [
              { query: LIST_FINANCE_PACKAGE_TYPES_QUERY },
              { query: LIST_FINANCE_PACKAGES_QUERY },
            ],
          });
          resolve(result);
        } catch (err) {
          reject(errorsHandler(err));
        }
      }),
    loading,
  };
};

export const useCreateFinancePackage = () => {
  const [mutate, { loading }] = useMutation(CREATE_FINANCE_PACKAGE_MUTATION);

  return {
    createPackage: (input: Record<string, unknown>) =>
      new Promise(async (resolve, reject) => {
        try {
          const result = await mutate({
            variables: { input },
            update(cache, { data }) {
              const pkg = data?.createFinancePackage;
              if (pkg) upsertFinancePackageInListCache(cache, pkg);
            },
            refetchQueries: [{ query: LIST_FINANCE_PACKAGES_QUERY }],
            awaitRefetchQueries: true,
          });
          resolve(result);
        } catch (err) {
          reject(errorsHandler(err));
        }
      }),
    loading,
  };
};

export const useUpdateFinancePackage = () => {
  const [mutate, { loading }] = useMutation(UPDATE_FINANCE_PACKAGE_MUTATION);

  return {
    updatePackage: (input: Record<string, unknown>) =>
      new Promise(async (resolve, reject) => {
        try {
          const result = await mutate({
            variables: { input },
            update(cache, { data }) {
              const pkg = data?.updateFinancePackage;
              if (pkg) upsertFinancePackageInListCache(cache, pkg);
            },
            refetchQueries: [{ query: LIST_FINANCE_PACKAGES_QUERY }],
            awaitRefetchQueries: true,
          });
          resolve(result);
        } catch (err) {
          reject(errorsHandler(err));
        }
      }),
    loading,
  };
};

export const useDeleteFinancePackage = () => {
  const [mutate, { loading }] = useMutation(DELETE_FINANCE_PACKAGE_MUTATION);

  return {
    deletePackage: (id: string) =>
      new Promise(async (resolve, reject) => {
        try {
          const result = await mutate({
            variables: { id },
            update(cache) {
              removeFinancePackageFromListCache(cache, id);
            },
            refetchQueries: [{ query: LIST_FINANCE_PACKAGES_QUERY }],
            awaitRefetchQueries: true,
          });
          resolve(result);
        } catch (err) {
          reject(errorsHandler(err));
        }
      }),
    loading,
  };
};

/** Always loads current line items from API (avoids stale list cache). */
export const useFreshFinancePackageLineItems = () => {
  const [loadPackage] = useLazyQuery<{
    getFinancePackage: FinancePackage | null;
  }>(GET_FINANCE_PACKAGE_QUERY, { fetchPolicy: "network-only" });

  return async (pkg: FinancePackage): Promise<FinanceQuoteLineItem[]> => {
    try {
      const { data } = await loadPackage({ variables: { id: pkg._id } });
      const fresh = data?.getFinancePackage;
      if (fresh?.lineItems?.length) {
        return packageToLineItems(fresh);
      }
    } catch {
      // fall back to cached list entry
    }
    return packageToLineItems(pkg);
  };
};

export { useListFinanceQuotes, useCreateFinanceQuote } from "./quotes";

export {
  useGetFinanceQuoteDetail,
  useUpdateFinanceQuote,
  useSendFinanceQuote,
  useDownloadFinanceQuotePdf,
  useDuplicateFinanceQuote,
  useDeleteFinanceQuote,
  useConvertFinanceQuoteToInvoice,
  useAssignWorkOrderToFinanceQuote,
  useListFinanceQuoteActivityLogs,
  useListOemEmailThreadsByFinanceQuoteLazy,
  useAttachFinanceQuotePurchaseOrder,
  useRemoveFinanceQuotePurchaseOrder,
} from "./quoteDetail";

export { useListFinanceInvoices, useCreateFinanceInvoice } from "./invoices";

export {
  useGetFinanceInvoiceDetail,
  useUpdateFinanceInvoice,
  useSendFinanceInvoice,
  useDownloadFinanceInvoicePdf,
  useDuplicateFinanceInvoice,
  useDeleteFinanceInvoice,
  useAssignWorkOrderToFinanceInvoice,
  useListFinanceInvoiceActivityLogs,
  useListEmailThreadsByFinanceInvoiceLazy,
  useAttachFinanceInvoicePurchaseOrder,
  useRemoveFinanceInvoicePurchaseOrder,
} from "./invoiceDetail";
